import { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Modal, Spinner } from 'react-bootstrap';
import { AuditEntry } from '@/pages/oms/orders/[id]/audit/AuditEntry';
import type { AuditHistoryEntry } from '@/infrastructure/OMSClient';
import { useOMS } from '@/apps/hooks/oms/useOMS';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import type { FulfillmentOrderEvent } from '@/infrastructure/FulfillmentAdminClient';
import { getAdminApiUrl, AdminApiDomain } from '@/config/ApiRouting';
import { kstTimestamp } from '@/utils/fulfillmentTime';
import { FulfillmentV2Launch } from '@/infrastructure/FulfillmentV2Launch';

const AUDIT_EMPTY = 'No audit history for this order yet.';
const AUDIT_FAILED = 'Could not load the audit history. Close and try again.';
const V2_ONLY_NOTE =
  'Fulfillment 2.0 history only: audit events since July 2026. Older history is migrated after launch.';

export interface AuditHistoryModalProps {
  show: boolean;
  orderId: string;
  /** True for an order the store owns; a 2.0-native order has no trail there to read [T08]. */
  inWooCommerce: boolean;
  onHide: () => void;
}

/** The OMS audit trail read where the order is read, off the same endpoint, so there is one history rather than two. */
export function AuditHistoryModal({ show, orderId, inWooCommerce, onHide }: AuditHistoryModalProps) {
  const oms = useOMS();
  const fulfillment = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const [entries, setEntries] = useState<AuditHistoryEntry[]>([]);
  const [events, setEvents] = useState<FulfillmentOrderEvent[]>([]);
  // Which trail is on screen: the store's for a commerce order until Fulfillment V2 is launched, then V2's for every order.
  const [readsStore, setReadsStore] = useState(inWooCommerce);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }
    let ignore = false;
    setLoading(true);
    setFailed(false);
    // Two trails, one per kind of order: the store keeps a commerce order's history and the
    // fulfillment event log is the whole record of an order the store never saw [T08]. Once
    // Fulfillment V2 is launched V1 no longer changes, so every order reads the V2 log (SSD-7090).
    const read = (inWooCommerce ? FulfillmentV2Launch.isLaunched() : Promise.resolve(true)).then((launched) => {
      if (ignore) {
        return;
      }
      setReadsStore(!launched);
      return launched
        ? fulfillment.listOrderEvents(Number(orderId)).then((log) => {
            if (!ignore) {
              setEvents(log.events);
            }
          })
        : oms.listAuditHistory(orderId).then((history) => {
            if (!ignore) {
              setEntries(history);
            }
          });
    });
    read
      .catch((error: unknown) => {
        if (!ignore) {
          console.error('Error fetching audit history:', error);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
    // The hook rebuilds its client every render, so depending on it here would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, orderId, inWooCommerce]);

  const rows = readsStore ? entries.length : events.length;

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          Audit history · <span className="font-monospace">#{orderId}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" role="status" aria-label="Loading audit history" />
          </div>
        )}
        {inWooCommerce && !readsStore && <div className="text-muted small mb-2">{V2_ONLY_NOTE}</div>}
        {failed && !loading && (
          <Alert variant="danger" className="mb-0">
            {AUDIT_FAILED}
          </Alert>
        )}
        {!loading && !failed && rows === 0 && <div className="text-muted">{AUDIT_EMPTY}</div>}
        {!loading &&
          !failed &&
          readsStore &&
          entries.map((entry, index) => <AuditEntry key={`${entry.updatedTimestamp}-${index}`} entry={entry} kst />)}
        {!loading &&
          !failed &&
          !readsStore &&
          events.map((event) => (
            <Card className="mb-3" key={event.id}>
              <Card.Body className="py-2">
                <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2">
                  <span className="font-monospace fw-semibold">{event.operation ?? '—'}</span>
                  <span className="text-muted small">
                    {event.operator ?? '—'} · {event.source ?? '—'} · {kstTimestamp(event.occurredAt)}
                  </span>
                </div>
                {event.payload && Object.keys(event.payload).length > 0 && (
                  <pre className="small mb-0 mt-1">{JSON.stringify(event.payload, null, 2)}</pre>
                )}
              </Card.Body>
            </Card>
          ))}
      </Modal.Body>
    </Modal>
  );
}
